import { useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Layer, Source, useMap } from 'react-map-gl';
import type { MapSourceDataEvent } from 'mapbox-gl';
import ErrorToast from 'components/error-toast/error-toast';
import { allDatasetAtoms } from '../atoms';
import {
  SUBSURFACE_VALUE_MAX_PARAM,
  SUBSURFACE_VALUE_MIN_PARAM,
  subsurfaceMapsConfig,
} from '../datasets/subsurface-maps.config';
import { valueRangeFrom } from '../filter-modal/value-histogram';
import {
  selectedSubsurfaceMapAtom,
  subsurfaceColorRuleAtom,
  subsurfaceColoringEnabledAtom,
  subsurfaceOpacityPercentAtom,
  subsurfaceRasterStatusAtom,
  subsurfaceShownGridRangeAtom,
} from './atoms';
import { cellsPaint } from './color-rule';
import { usePetrotechLayerOrder } from './layer-order';
import {
  CELLS_LAYER_ID,
  CELLS_SOURCE_ID,
  CELLS_SOURCE_LAYER,
  CELLS_SOURCE_MAX_ZOOM,
} from './utils';
import type { SubsurfaceMapRow } from './types';

const datasetAtoms = allDatasetAtoms.subsurfaceMaps;

/**
 * The cells tile URL for a map, as the dataset tile layers build theirs.
 * @param {number} mapId The map.
 * @return {string} The tile URL template.
 */
export function cellsTilesUrl(mapId: number): string {
  return `${window.location.origin}${subsurfaceMapsConfig.tileEndpoint}/cells/{z}/{x}/{y}?map_id=${mapId}`;
}

/**
 * Whether a map's [west, south, east, north] extent overlaps the viewport bbox.
 * A map without an extent (stats not built) can't be placed, so it counts as
 * overlapping — the map is only dropped when a filter provably excludes it.
 * @param {number[] | null} extent The shown map's extent.
 * @param {object} box The payload's visible-extent bbox.
 * @return {boolean} True when they overlap (or it can't be ruled out).
 */
function extentIntersects(
  extent: SubsurfaceMapRow['extent'],
  box: { sw_lng: number; sw_lat: number; ne_lng: number; ne_lat: number }
): boolean {
  if (!extent) return true;
  const [west, south, east, north] = extent;
  return (
    west <= box.ne_lng &&
    east >= box.sw_lng &&
    south <= box.ne_lat &&
    north >= box.sw_lat
  );
}

/**
 * Draws the selected subsurface map's cells on the map, the way structure grid
 * surfaces draw: a vector tile source of value cells and a fill layer whose paint
 * expression colours them. A colour rule, the value filter and transparency are
 * paint changes, so they never refetch. Hides when the dataset is off, colouring
 * is disabled, or the filters leave the map out.
 * @return {JSX.Element | null} The source and fill layer, or a toast.
 */
export const SubsurfaceCellsLayer = () => {
  const [shown, setShown] = useAtom(selectedSubsurfaceMapAtom);
  const setStatus = useSetAtom(subsurfaceRasterStatusAtom);
  const setGridRange = useSetAtom(subsurfaceShownGridRangeAtom);
  const rule = useAtomValue(subsurfaceColorRuleAtom);
  const opacityPercent = useAtomValue(subsurfaceOpacityPercentAtom);
  const coloringEnabled = useAtomValue(subsurfaceColoringEnabledAtom);
  const datasetVisible = useAtomValue(datasetAtoms.onMapAtom);
  const search = useAtomValue(datasetAtoms.searchAtom) as {
    data?: { results?: { id: number }[]; totalCount?: number };
    currentPayload?: Record<string, unknown>;
    loading?: boolean;
    error?: unknown;
  };
  const { current: mapRef } = useMap();
  const [failedName, setFailedName] = useState<string | null>(null);

  // Drop the map only when a complete result set leaves it out: a map on another
  // page stays, and so does one merely panned out of view in visible-extent mode
  // (its extent misses the payload bbox).
  const results = search.data?.results;
  const totalCount = search.data?.totalCount;
  const payloadBox = search.currentPayload?.bounding_box as
    | { sw_lng: number; sw_lat: number; ne_lng: number; ne_lat: number }
    | undefined;
  // The payload changes the moment a search starts, the results only when it
  // answers; mid-flight they describe different searches (panning a map back into
  // view would read as "filtered out"), so this waits for the answer. A failed
  // search keeps the old results under the new payload, so it decides nothing.
  const searching = !!search.loading;
  const searchFailed = !!search.error;
  useEffect(() => {
    if (searching || searchFailed) return;
    const complete =
      results !== undefined &&
      totalCount !== undefined &&
      results.length >= totalCount;
    if (!shown || !complete || results.some((row) => row.id === shown.id)) {
      return;
    }
    if (payloadBox && !extentIntersects(shown.extent, payloadBox)) return;
    setShown(null);
  }, [
    searching,
    searchFailed,
    results,
    totalCount,
    shown,
    setShown,
    payloadBox,
  ]);

  const onMap = !!shown && datasetVisible && coloringEnabled;

  // The shown map's lifecycle: loading until its tiles are in, then shown. Mapbox
  // reports a new source loaded at its metadata stage, before any tile, so only a
  // tile's own load (`event.tile`) counts; a map outside the view never loads a
  // tile, so for it the map settling (idle) counts. A basemap switch re-adds the
  // source, so the map is loading again until its tiles are back. A tile the
  // server refuses while loading clears the selection with a toast (a lapsed
  // session re-authenticates, as the api client does); once the map is up, one
  // refused tile is Mapbox's to retry on the next pan.
  const shownId = shown?.id ?? null;
  const shownExtent = shown?.extent ?? null;
  const needsGridRange =
    !!shown && (shown.valueMin === null || shown.valueMax === null);
  useEffect(() => {
    setGridRange(null);
    const map = mapRef?.getMap();
    if (!onMap || !map) {
      setStatus('hidden');
      return undefined;
    }
    setStatus('loading');
    let tileSeen = false;
    let isShown = false;
    let rangeRead = false;

    // A map without stats is painted across its grid's own range, which every
    // cell carries; the legend labels it from here. Read once: the query walks
    // every loaded cell.
    const readGridRange = () => {
      if (!needsGridRange || rangeRead) return;
      const cell = map.querySourceFeatures(CELLS_SOURCE_ID, {
        sourceLayer: CELLS_SOURCE_LAYER,
      })[0]?.properties;
      if (cell && typeof cell.min_val === 'number') {
        rangeRead = true;
        setGridRange([cell.min_val, cell.max_val as number]);
      }
    };
    const loaded = () => {
      isShown = true;
      setStatus('shown');
      readGridRange();
    };
    const inView = () => {
      // Without an extent (stats pending) the map can't be placed, so it counts
      // as in view only until the map settles.
      if (!shownExtent) return false;
      const bounds = map.getBounds();
      return extentIntersects(shownExtent, {
        sw_lng: bounds.getWest(),
        sw_lat: bounds.getSouth(),
        ne_lng: bounds.getEast(),
        ne_lat: bounds.getNorth(),
      });
    };
    const onSourceData = (event: MapSourceDataEvent) => {
      if (
        event.sourceId !== CELLS_SOURCE_ID ||
        !event.tile ||
        !event.isSourceLoaded
      ) {
        return;
      }
      tileSeen = true;
      loaded();
    };
    const onIdle = () => {
      if (
        !map.getSource(CELLS_SOURCE_ID) ||
        !map.isSourceLoaded(CELLS_SOURCE_ID)
      ) {
        return;
      }
      // Settled with no tile yet: shown only when Mapbox had nothing to request,
      // which is a map lying outside the view.
      if (tileSeen || !inView()) loaded();
    };
    const onStyleLoad = () => {
      tileSeen = false;
      isShown = false;
      setStatus('loading');
    };
    const onError = (event: {
      sourceId?: string;
      error?: { status?: number };
    }) => {
      if (event.sourceId !== CELLS_SOURCE_ID) return;
      if (event.error?.status === 401) {
        window.location.reload();
        return;
      }
      if (isShown) return;
      setFailedName(shown.name);
      setShown(null);
    };
    map.on('sourcedata', onSourceData);
    map.on('idle', onIdle);
    map.on('style.load', onStyleLoad);
    map.on('error', onError);
    return () => {
      map.off('sourcedata', onSourceData);
      map.off('idle', onIdle);
      map.off('style.load', onStyleLoad);
      map.off('error', onError);
    };
    // Re-run only when a different map is shown or it leaves the map; the row
    // object itself is rebuilt on every search refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownId, onMap, mapRef]);

  // The Petrotech layers sit on the basemap under everything else, the shown map's
  // cells above the footprints (INS-13193). Kept in place across toggles, map
  // changes and basemap switches.
  usePetrotechLayerOrder(mapRef?.getMap());

  // The applied value filter; cells outside it are dimmed. Disabling the
  // filter moves it out of the payload, so this follows Disable too.
  const valueRange = valueRangeFrom(
    search.currentPayload,
    SUBSURFACE_VALUE_MIN_PARAM,
    SUBSURFACE_VALUE_MAX_PARAM
  );
  const maskLow = valueRange?.low;
  const maskHigh = valueRange?.high;
  const mask = useMemo(() => {
    if (maskLow === undefined || maskHigh === undefined) return null;
    return { low: maskLow, high: maskHigh };
  }, [maskLow, maskHigh]);

  const paint = useMemo(
    () => (shown ? cellsPaint(shown, rule, mask, opacityPercent) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shown?.id, shown?.valueMin, shown?.valueMax, rule, mask, opacityPercent]
  );

  return (
    <>
      {failedName && (
        <ErrorToast
          message="Couldn't load this Petrotech map"
          subtitle={failedName}
          onClose={() => {
            setFailedName(null);
            return true;
          }}
        />
      )}
      {onMap && paint && (
        <Source
          key={shown.id}
          id={CELLS_SOURCE_ID}
          type="vector"
          tiles={[cellsTilesUrl(shown.id)]}
          maxzoom={CELLS_SOURCE_MAX_ZOOM}
          {...(shown.extent ? { bounds: shown.extent } : {})}
        >
          <Layer
            id={CELLS_LAYER_ID}
            type="fill"
            source-layer={CELLS_SOURCE_LAYER}
            paint={paint}
          />
        </Source>
      )}
    </>
  );
};
